import React, { useMemo, useState } from "react";
import { useI18n } from "../i18n";
import {
  type CalendarTag,
  type RepeatRule,
  addCalendarEntry,
  excludeOccurrence,
  getOccurrencesForDate,
  getOccurrencesInRange,
  loadCalendarEntries,
  removeCalendarEntry,
  todayISO,
} from "../calendar/store";

type TagDef = {
  tag: CalendarTag;
  label: string;
  icon: string;
  tone: "muted" | "primary" | "danger" | "emerald" | "rose" | "amber" | "blue" | "violet";
  repeatable?: boolean;
  group: "proposal" | "markers";
};

function startOfMonthISO(y: number, m0: number) {
  const d = new Date(y, m0, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function endOfMonthISO(y: number, m0: number) {
  const d = new Date(y, m0 + 1, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function CalendarPage() {
  const { t } = useI18n();

  const [selected, setSelected] = useState<string>(todayISO());
  const [cursor, setCursor] = useState(() => {
    const [y, m] = todayISO().split("-").map(Number);
    return { y, m0: (m || 1) - 1 };
  });

  // App behavior: allow disarm (null) + a global repeat picker:contentReference[oaicite:8]{index=8}
  const [armedTag, setArmedTag] = useState<CalendarTag | null>(null);
  const [repeat, setRepeat] = useState<RepeatRule>("none");
  const [rev, setRev] = useState(0);

  // Cycle span modal for period marker:contentReference[oaicite:9]{index=9}
  const [cycleModal, setCycleModal] = useState(false);
  const [cycleDays, setCycleDays] = useState("5");

  const tags: TagDef[] = useMemo(
    () => [
      // Proposal markers (web-specific; keep)
      { tag: "date_open", label: t("calendar.tags.open") || "Open proposal", icon: "🟢", tone: "emerald", group: "proposal" },
      { tag: "date_closed", label: t("calendar.tags.closed") || "Closed / done", icon: "🔴", tone: "rose", group: "proposal" },

      // App markers (mirror app UI list):contentReference[oaicite:10]{index=10}
      { tag: "unavailable", label: t("calendar.tags.unavailable") || "Unavailable", icon: "🚫", tone: "muted", repeatable: true, group: "markers" },
      { tag: "date_night", label: t("calendar.tags.date_night") || "Date", icon: "❤️", tone: "primary", repeatable: true, group: "markers" },
      { tag: "period", label: t("calendar.tags.period") || "Cycle", icon: "🩸", tone: "danger", repeatable: true, group: "markers" },
      { tag: "sick", label: t("calendar.tags.sick") || "Sick", icon: "🤒", tone: "amber", group: "markers" },
      { tag: "flirty", label: t("calendar.tags.flirty") || "Flirty", icon: "💬", tone: "blue", group: "markers" },
      { tag: "passionate", label: t("calendar.tags.passionate") || "Passion", icon: "🔥", tone: "rose", group: "markers" },
      { tag: "intimate", label: t("calendar.tags.intimate") || "Intimate", icon: "✨", tone: "violet", group: "markers" },
    ],
    [t],
  );

  const monthFrom = startOfMonthISO(cursor.y, cursor.m0);
  const monthTo = endOfMonthISO(cursor.y, cursor.m0);

  const occInMonth = useMemo(() => {
    void rev; // force reload on change
    return getOccurrencesInRange(monthFrom, monthTo);
  }, [monthFrom, monthTo, rev]);

  const occByDay = useMemo(() => {
    const m = new Map<string, CalendarTag[]>();
    for (const o of occInMonth) {
      const a = m.get(o.date) ?? [];
      a.push(o.entry.tag);
      m.set(o.date, a);
    }
    return m;
  }, [occInMonth]);

  const monthLabel = useMemo(() => {
    const dt = new Date(cursor.y, cursor.m0, 1);
    return dt.toLocaleString(undefined, { month: "long", year: "numeric" });
  }, [cursor.y, cursor.m0]);

  const grid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m0, 1);
    const last = new Date(cursor.y, cursor.m0 + 1, 0);
    const dow0 = (first.getDay() + 6) % 7; // Monday-first like app
    const daysInMonth = last.getDate();

    const cells: { iso: string; day: number; inMonth: boolean }[] = [];
    for (let i = 0; i < dow0; i++) cells.push({ iso: "", day: 0, inMonth: false });
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(cursor.y, cursor.m0, d);
      const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
      cells.push({ iso, day: d, inMonth: true });
    }
    while (cells.length % 7 !== 0) cells.push({ iso: "", day: 0, inMonth: false });
    return cells;
  }, [cursor.y, cursor.m0]);

  function setCursorPrev() {
    setCursor((c) => {
      const m0 = c.m0 - 1;
      if (m0 < 0) return { y: c.y - 1, m0: 11 };
      return { y: c.y, m0 };
    });
  }
  function setCursorNext() {
    setCursor((c) => {
      const m0 = c.m0 + 1;
      if (m0 > 11) return { y: c.y + 1, m0: 0 };
      return { y: c.y, m0 };
    });
  }

  function hasTagOnDate(dateISO: string, tag: CalendarTag): boolean {
    const tagsForDay = occByDay.get(dateISO) ?? [];
    return tagsForDay.includes(tag);
  }

  // Remove tag occurrences on a specific date:
  // - if repeating occurrence not on base date: excludeOccurrence
  // - else removeCalendarEntry
  function removeTagOnDate(dateISO: string, tag: CalendarTag) {
    const occ = getOccurrencesForDate(dateISO).filter((o) => o.entry.tag === tag);
    for (const o of occ) {
      const e = o.entry;
      if ((e.repeat ?? "none") !== "none" && o.date !== e.date) {
        excludeOccurrence(e.id, dateISO);
      } else {
        removeCalendarEntry(e.id);
      }
    }
  }

  function toggleProposalExclusive(dateISO: string, tag: "date_open" | "date_closed") {
    const opposite = tag === "date_open" ? "date_closed" : "date_open";
    // remove opposite first
    if (hasTagOnDate(dateISO, opposite)) removeTagOnDate(dateISO, opposite);
    // then toggle target
    if (hasTagOnDate(dateISO, tag)) removeTagOnDate(dateISO, tag);
    else addCalendarEntry({ date: dateISO, tag, note: undefined, repeat: "none", spanDays: 1 });
    setRev((v) => v + 1);
  }

  function toggleTag(dateISO: string, tag: CalendarTag) {
    if (!dateISO) return;

    // Cycle span modal for period, like app:contentReference[oaicite:11]{index=11}
    if (tag === "period") {
      setSelected(dateISO);
      setArmedTag(tag);
      setCycleModal(true);
      return;
    }

    // web proposal exclusivity
    if (tag === "date_open" || tag === "date_closed") {
      toggleProposalExclusive(dateISO, tag);
      return;
    }

    if (hasTagOnDate(dateISO, tag)) {
      removeTagOnDate(dateISO, tag);
      setRev((v) => v + 1);
      return;
    }

    addCalendarEntry({ date: dateISO, tag, note: undefined, repeat, spanDays: 1 });
    setRev((v) => v + 1);
  }

  function onPressDay(iso: string) {
    if (!iso) return;
    setSelected(iso);
    if (armedTag) toggleTag(iso, armedTag);
  }

  // App behavior: click same marker again => disarm; else arm and apply immediately to selected date:contentReference[oaicite:12]{index=12}
  function onPressLegend(tag: CalendarTag) {
    if (armedTag === tag) {
      setArmedTag(null);
      return;
    }
    setArmedTag(tag);
    toggleTag(selected, tag);
  }

  function setCycleSpanAndSave() {
    const n = Math.max(1, Math.min(21, Number(cycleDays || "5") || 5));

    // app behavior: if any period marker exists on selected date, remove; else add spanDays n:contentReference[oaicite:13]{index=13}
    if (hasTagOnDate(selected, "period")) {
      removeTagOnDate(selected, "period");
      setCycleModal(false);
      setRev((v) => v + 1);
      return;
    }

    addCalendarEntry({ date: selected, tag: "period", note: undefined, repeat, spanDays: n });
    setCycleModal(false);
    setCycleDays("5");
    setRev((v) => v + 1);
  }

  const selectedOcc = useMemo(() => {
    void rev;
    return getOccurrencesForDate(selected);
  }, [selected, rev]);

  const uniqSelectedTags = useMemo(() => {
    const set = new Set<CalendarTag>();
    for (const o of selectedOcc) set.add(o.entry.tag);
    return Array.from(set);
  }, [selectedOcc]);

  function toneClass(tone: TagDef["tone"], active: boolean) {
    // Conservative Tailwind palette (works even before your theme-variable migration).
    const base = "px-3 py-2 rounded-full border text-xs font-semibold flex items-center gap-2 max-w-[48%] ";
    const a = active ? "ring-2 ring-pink-500/60 " : "";
    switch (tone) {
      case "emerald":
        return base + a + (active ? "border-emerald-400 bg-emerald-500/15 text-emerald-200" : "border-slate-800 bg-slate-950/40 text-slate-200");
      case "rose":
        return base + a + (active ? "border-rose-400 bg-rose-500/15 text-rose-200" : "border-slate-800 bg-slate-950/40 text-slate-200");
      case "danger":
        return base + a + (active ? "border-red-400 bg-red-500/15 text-red-200" : "border-slate-800 bg-slate-950/40 text-slate-200");
      case "amber":
        return base + a + (active ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-slate-800 bg-slate-950/40 text-slate-200");
      case "blue":
        return base + a + (active ? "border-blue-400 bg-blue-500/15 text-blue-200" : "border-slate-800 bg-slate-950/40 text-slate-200");
      case "violet":
        return base + a + (active ? "border-violet-400 bg-violet-500/15 text-violet-200" : "border-slate-800 bg-slate-950/40 text-slate-200");
      case "primary":
        return base + a + (active ? "border-pink-400 bg-pink-500/15 text-pink-200" : "border-slate-800 bg-slate-950/40 text-slate-200");
      case "muted":
      default:
        return base + a + (active ? "border-slate-400 bg-slate-500/10 text-slate-100" : "border-slate-800 bg-slate-950/40 text-slate-200");
    }
  }

  const repeatBtn = (val: RepeatRule, label: string) => {
    const active = repeat === val;
    return (
      <button
        type="button"
        onClick={() => setRepeat(val)}
        className={
          "px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors " +
          (active ? "border-pink-500 bg-pink-500/15 text-pink-200" : "border-slate-800 bg-slate-950/40 text-slate-200 hover:border-slate-600")
        }
      >
        {label}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold">{t("calendar.title") || "Calendar"}</h1>
        <p className="text-sm text-slate-400">
          {t("calendar.subtitle") || "Tap a marker to arm it, then tap a day. Tap the same marker again to disarm."}
        </p>
      </header>

      {/* Repeat picker */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="text-xs text-slate-400 mr-2">{t("calendar.repeat.label") || "Repeat:"}</div>
        {repeatBtn("none", t("calendar.repeat.none") || "None")}
        {repeatBtn("weekly", t("calendar.repeat.weekly") || "Weekly")}
        {repeatBtn("monthly", t("calendar.repeat.monthly") || "Monthly")}
      </div>

      {/* Legend */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="text-sm font-semibold">{t("calendar.legend.proposal") || "Date proposal"}</div>
        <div className="flex flex-wrap gap-2">
          {tags.filter((x) => x.group === "proposal").map((d) => {
            const active = armedTag === d.tag;
            return (
              <button key={d.tag} type="button" onClick={() => onPressLegend(d.tag)} className={toneClass(d.tone, active)}>
                <span aria-hidden>{d.icon}</span>
                <span className="truncate">{d.label}{active ? " ✓" : ""}</span>
              </button>
            );
          })}
        </div>

        <div className="text-sm font-semibold pt-2">{t("calendar.legend.markers") || "Markers"}</div>
        <div className="flex flex-wrap gap-2">
          {tags.filter((x) => x.group === "markers").map((d) => {
            const active = armedTag === d.tag;
            return (
              <button key={d.tag} type="button" onClick={() => onPressLegend(d.tag)} className={toneClass(d.tone, active)}>
                <span aria-hidden>{d.icon}</span>
                <span className="truncate">{d.label}{active ? " ✓" : ""}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={setCursorPrev}
          className="px-3 py-2 rounded-xl border border-slate-800 bg-slate-950/40 text-slate-200 text-sm hover:border-slate-600"
        >
          {t("calendar.prev") || "Prev"}
        </button>
        <div className="text-sm font-semibold text-slate-100">{monthLabel}</div>
        <button
          type="button"
          onClick={setCursorNext}
          className="px-3 py-2 rounded-xl border border-slate-800 bg-slate-950/40 text-slate-200 text-sm hover:border-slate-600"
        >
          {t("calendar.next") || "Next"}
        </button>
      </div>

      {/* Grid */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-3">
        <div className="grid grid-cols-7 gap-2 text-[11px] text-slate-400 mb-2">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="text-center">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {grid.map((c, idx) => {
            const isSel = c.iso && c.iso === selected;
            const tagsForDay = c.iso ? (occByDay.get(c.iso) ?? []) : [];
            const uniq = Array.from(new Set(tagsForDay)).slice(0, 4);

            if (!c.inMonth) return <div key={idx} className="h-14 rounded-xl" />;

            return (
              <button
                key={c.iso}
                type="button"
                onClick={() => onPressDay(c.iso)}
                className={
                  "h-14 rounded-xl border p-2 text-left transition-colors " +
                  (isSel ? "border-pink-500 bg-pink-500/10" : "border-slate-800 bg-slate-950/40 hover:border-slate-600")
                }
              >
                <div className="text-xs font-semibold text-slate-100">{c.day}</div>
                <div className="mt-1 flex gap-1 flex-wrap">
                  {uniq.map((tag) => (
                    <span
                      key={tag}
                      className="px-1.5 py-0.5 rounded-full text-[10px] border border-slate-700 bg-slate-900/40 text-slate-200"
                      title={String(tag)}
                    >
                      {String(tag).toUpperCase().replaceAll("_", " ").slice(0, 8)}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day panel */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="text-sm font-semibold">{(t("calendar.selected") || "Selected") + `: ${selected}`}</div>

        <div className="text-xs text-slate-400">
          {uniqSelectedTags.length
            ? (t("calendar.selectedHelpHas") || "Tap a marker below to remove it from this date.")
            : (t("calendar.selectedHelpNone") || "No markers yet. Arm a marker above and tap a day.")}
        </div>

        {!!uniqSelectedTags.length && (
          <div className="flex flex-wrap gap-2">
            {uniqSelectedTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(selected, tag)}
                className="px-3 py-2 rounded-full border border-slate-800 bg-slate-950/40 text-slate-100 text-xs font-bold"
              >
                {String(tag)} <span className="text-rose-300">×</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Cycle span modal (period) */}
      {cycleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.55)" }}>
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-950 p-4">
            <div className="text-base font-extrabold text-slate-100">{t("calendar.cycle.title") || "Cycle length"}</div>
            <div className="text-sm text-slate-400 mt-1">{t("calendar.cycle.subtitle") || "How many days should this marker span?"}</div>

            <div className="mt-3">
              <label className="text-xs text-slate-400">{t("calendar.cycle.days") || "Days (1–21)"}</label>
              <input
                value={cycleDays}
                onChange={(e) => setCycleDays(e.target.value)}
                inputMode="numeric"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-800 bg-slate-900/40 text-slate-100"
              />
            </div>

            <div className="mt-4 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setCycleModal(false)}
                className="px-4 py-2 rounded-xl border border-slate-800 bg-slate-900/40 text-slate-200 text-sm"
              >
                {t("common.cancel") || "Cancel"}
              </button>
              <button
                type="button"
                onClick={setCycleSpanAndSave}
                className="px-4 py-2 rounded-xl bg-pink-500 text-white text-sm font-semibold hover:bg-pink-400"
              >
                {t("common.save") || "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
